import { css } from '@repo/pandacss/styled-system/css';
import { DriverType } from '@repo/supergraph/marketplace/types';
import type { SummaryTableRowType } from '@repo/supergraph/roundup/base/types';
import React from 'react';
import ApyDriver from './ApyDriver';

interface CdApysDriverProps {
  dividerSpacing?: 1 | 2 | 3 | 4 | 5;
  driver?: DriverType;
  oneYearApyEntity?: SummaryTableRowType['oneYearApyEntity'];
  threeYearApyEntity?: SummaryTableRowType['threeYearApyEntity'];
  fiveYearApyEntity?: SummaryTableRowType['fiveYearApyEntity'];
  tooltipClassName?: string;
  /** Summary table: single APY column shows rate only (no "APY " prefix). */
  suppressApyTermPrefix?: boolean;
  shouldApplyStylingUpdates?: boolean;
  /** Icon size: 16 for product card, 14 for summary table. Defaults to 16. */
  iconSize?: 14 | 16;
}

export const CdApysDriver = ({
  driver,
  oneYearApyEntity,
  threeYearApyEntity,
  fiveYearApyEntity,
  dividerSpacing = 3,
  tooltipClassName,
  suppressApyTermPrefix = false,
  shouldApplyStylingUpdates = false,
  iconSize = 16,
}: CdApysDriverProps): React.JSX.Element => {
  const traditionalTerms = [
    {
      ...oneYearApyEntity?.driverApy,
      label: '1-year APY',
    },
    {
      ...threeYearApyEntity?.driverApy,
      label: '3-year APY',
    },
    {
      ...fiveYearApyEntity?.driverApy,
      label: '5-year APY',
    },
  ];
  const apyDrivers = traditionalTerms?.filter(
    /*
      Only render drivers that have values attached to them. There are
      some uses cases where query0 can return empty drivers
    */
    driver => driver?.id?.includes('apy') && driver.value,
  );
  const hasMultiAPYDrivers = !!apyDrivers && apyDrivers.length > 0;
  // For summary table if there is only one APY value the APY prefix is rendered
  const finalDrivers = hasMultiAPYDrivers ? apyDrivers : driver ? [driver] : [];

  if (!finalDrivers || finalDrivers.length === 0) {
    return <span>N/A</span>;
  }

  const dividerClassName = css({
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    borderTopWidth: 'thin',
    borderTopStyle: 'solid',
    borderTopColor: 'neutral.lighter',
    width: 'full',
    marginTop: '0',
    marginRight: '0',
    marginBottom: '0',
    marginLeft: '0',
    marginBlockStart: `${dividerSpacing}`,
    marginBlockEnd: `${dividerSpacing}`,
  });

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: shouldApplyStylingUpdates ? 'center' : 'flex-start',
      })}
    >
      {finalDrivers?.map((driver, index) => {
        if (!!driver?.label && !!driver?.value) {
          return (
            <React.Fragment key={`${driver.id}-${index}`}>
              {index > 0 && <hr className={dividerClassName} />}
              <ApyDriver
                termLength={driver.label}
                termLengthValue={driver.value}
                tooltipClassName={tooltipClassName}
                valueTooltip={driver.valueTooltip}
                suppressApyTermPrefix={suppressApyTermPrefix}
                iconSize={iconSize}
              />
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={`n-a-${index}`}>
            {index > 0 && <hr className={dividerClassName} />}
            <span>N/A</span>
          </React.Fragment>
        );
      })}
    </div>
  );
};
